/**
 * MobileBottomNavigation — Accesos frecuentes para pantallas táctiles pequeñas.
 * Deriva rutas y autorizaciones del menú central; no mantiene permisos propios.
 */
import { Receipt } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks";
import { navigationGuard } from "@/lib/utils/navigationGuard";
import { preloadRoute } from "@/lib/routePreloads";
import { SAFE_AREA } from "@/lib/nativeShell";
import { NAV_ITEMS, type NavItem } from "./navigation.config";

interface MobileNavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  source: NavItem;
  activePaths?: string[];
}

const itemByPath = (path: string): NavItem => {
  const item = NAV_ITEMS.find((candidate) => candidate.path === path);
  if (!item) throw new Error(`Ruta móvil base no encontrada: ${path}`);
  return item;
};

const dashboardItem = itemByPath("/dashboard");
const cajaItem = itemByPath("/caja");
const inventoryItem = itemByPath("/inventario");
const metricsItem = itemByPath("/metricas");
const salesItem = cajaItem.children?.find(
  (child) => child.path === "/caja/ventas",
);

if (!salesItem) {
  throw new Error("La ruta de ventas individuales debe existir en Caja.");
}

/**
 * Base funcional para todos los planes. Los módulos adicionales viven en el
 * drawer y no cambian estos cinco accesos al contratar otro plan.
 */
const GENERAL_MOBILE_ITEMS: MobileNavItem[] = [
  {
    label: dashboardItem.label,
    path: dashboardItem.path,
    icon: dashboardItem.icon,
    source: dashboardItem,
  },
  {
    label: cajaItem.label,
    path: cajaItem.path,
    icon: cajaItem.icon,
    source: cajaItem,
  },
  {
    label: "Ventas",
    path: salesItem.path,
    icon: Receipt,
    source: {
      ...cajaItem,
      roles: salesItem.roles,
      permissionKey: salesItem.permissionKey,
      excludeRoles: salesItem.excludeRoles,
    },
    activePaths: [salesItem.path],
  },
  {
    label: inventoryItem.label,
    path: inventoryItem.path,
    icon: inventoryItem.icon,
    source: inventoryItem,
  },
  {
    label: metricsItem.label,
    path: metricsItem.path,
    icon: metricsItem.icon,
    source: metricsItem,
  },
];

const isItemActive = (item: MobileNavItem, pathname: string): boolean =>
  item.activePaths?.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  ) ??
  (pathname === item.path ||
    pathname.startsWith(`${item.path}/`) ||
    item.source.children?.some(
      (child) =>
        pathname === child.path || pathname.startsWith(`${child.path}/`),
    ) === true);

export default function MobileBottomNavigation() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canAccess } = usePermissions();

  const items = GENERAL_MOBILE_ITEMS.filter((item) =>
    canAccess(
      item.source.roles,
      item.source.permissionKey,
      item.source.excludeRoles,
    ),
  );

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Navegación principal móvil"
      className={`${SAFE_AREA.mobileBottomNav} fixed inset-x-0 bottom-0 z-40 grid border-t border-gray-200 bg-white/95 px-0.5 shadow-[0_-3px_12px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 lg:hidden`}
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((item) => {
        const active = isItemActive(item, pathname);
        return (
          <button
            key={item.path}
            type="button"
            aria-current={active ? "page" : undefined}
            onMouseEnter={() => preloadRoute(item.path)}
            onFocus={() => preloadRoute(item.path)}
            onClick={() => {
              if (active) return;
              navigationGuard.check(() => navigate(item.path));
            }}
            className={`flex min-h-10 touch-manipulation flex-col items-center justify-center rounded-md px-0.5 text-[9px] font-semibold leading-tight transition-colors ${
              active
                ? "text-primary-700 dark:text-primary-300"
                : "text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800"
            }`}
          >
            <item.icon size={17} strokeWidth={active ? 2.5 : 2} aria-hidden />
            <span className="max-w-full truncate">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
